"use client";

import clsx from "clsx";
import {motion} from "framer-motion";

/**
 * A suggestion chip. Pass the object form when two chips can share a label —
 * the callback then hands back the `id`, so the caller resolves the right chip
 * instead of matching on display text.
 */
export type FollowUpSuggestion = {id: string; label: string};

interface FollowUpSuggestionsProps {
   suggestions: (string | FollowUpSuggestion)[];
   /** `id` is the suggestion's own id, or its label when passed as a string. */
   onSuggestionClick: (label: string, id: string) => void;
   className?: string;
}

function toSuggestion(suggestion: string | FollowUpSuggestion): FollowUpSuggestion {
   return typeof suggestion === "string"
      ? {id: suggestion, label: suggestion}
      : suggestion;
}

const FollowUpSuggestions: React.FC<FollowUpSuggestionsProps> = ({
   suggestions,
   onSuggestionClick,
   className,
}) => {
   if (suggestions.length === 0) return null;

   return (
      <div className={clsx("flex flex-wrap gap-2 mt-4", className)}>
         {suggestions.map(toSuggestion).map(({id, label}, index) => (
            <motion.button
               key={id}
               initial={{opacity: 0, y: 6, scale: 0.95}}
               animate={{opacity: 1, y: 0, scale: 1}}
               transition={{duration: 0.3, delay: index * 0.05, ease: [0.16, 1, 0.3, 1]}}
               onClick={() => onSuggestionClick(label, id)}
               className={clsx(
                  "px-3 py-1.5 text-sm rounded-lg border border-neutral bg-background",
                  "text-700 transition-all duration-200",
                  "hover:bg-100 hover:text-900 hover:border-300",
                  "active:scale-[0.97]",
                  "focus:outline-none focus:ring-2 focus:ring-primary"
               )}
            >
               {label}
            </motion.button>
         ))}
      </div>
   );
};

export default FollowUpSuggestions;
